'use client';

import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import { Configure, Hits, RefinementList } from 'react-instantsearch';
import SearchEmptyList from 'components/Search/components/SearchEmptyList';
import { DAYS_OF_WEEK, MONTHS } from 'constants/date';
import { convertObjectToQueryParam } from 'utils/misc';
import { Location } from 'location';
import { Button, EventCard } from '@sympla/sympla-components';
import { cardTypeTrackingSearch, componentTrackingSearch, typeTrackingSearch } from 'constants/search';
import { MATRIX_TYPE } from 'constants/index';
import { createPropsToTrackingMatrix } from 'components/Integration/createPropsToTrackingMatrix';
import locationStore from 'stores/LocationStore';

import * as styles from './AlgoliaSearchDropdown.css';

const SearchItem = dynamic(() => import('components/Search/components/SearchItem'), { ssr: false });

type AlgoliaSearchDropdownProps = {
  isDropdownOpen: boolean;
  inputValue?: string;
  closedDropdown: () => void;
  isNavbarCollapsed: boolean;
  userLocation: Location
  handleShowMore: () => void;
  lastSearches: string[];
  updateLastSearches: (text: string) => void;
};

function Hit({ hit }: { hit: IHit }) {
  const {
    objectID, name, organizer, images, event_url, start_date, end_date, __position, location, start_date_formats, end_date_formats, type,
  } = hit;

  const formatDate = (timestamp: number) => {
    const dateObject = new Date(timestamp * 1000);

    const dayOfWeek = DAYS_OF_WEEK[dateObject.getDay()];
    const dayOfMonth = dateObject.getDate();
    const month = MONTHS[dateObject.getMonth()];
    const hours = dateObject.getHours();
    const minutes = dateObject.getMinutes();

    const formattedHours = hours < 10 ? `0${hours}` : hours;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

    const formattedDate = `${dayOfWeek}, ${dayOfMonth} ${month} as ${formattedHours}h${formattedMinutes}`;
    return formattedDate;
  };

  const { dataCreative } = createPropsToTrackingMatrix(__position, {
    trackingPrefix: MATRIX_TYPE.SEARCH,
    matrizTemplate: '',
    dataLocation: locationStore.selectedLocation,
    cardType: cardTypeTrackingSearch.eventCardSearch,
    componentTemplate: componentTrackingSearch.termNavBar,
    type: typeTrackingSearch.event,
    payloadCard: hit,

  });

  return (
    <EventCard
      id={objectID}
      name={name}
      images={images}
      sectionPosition={__position}
      dataCreative={dataCreative}
      location={{
        name: location?.name || '',
        city: location?.city || '',
        state: location?.state || '',
      }}
      organizer={organizer}
      type={type}
      url={event_url}
      data
      start_date={formatDate(start_date)}
      start_date_formats={start_date_formats}
      end_date={formatDate(end_date)}
      end_date_formats={end_date_formats}
      layoutCard="lateral"
      key={objectID}
      fullWidth
    />
  );
}

const options = [
  { value: 'Eventos', tab: 'eventos' },
  { value: 'Locais', tab: 'locais' },
  { value: 'Produtores', tab: 'produtores' },
];

function AlgoliaSearchDropdown({
  isDropdownOpen,
  inputValue = '',
  closedDropdown,
  isNavbarCollapsed,
  userLocation,
  handleShowMore,
  lastSearches,
  updateLastSearches,
}: AlgoliaSearchDropdownProps) {
  const dropdownContainerClasses = `${styles.dropdownWrapper} ${
    isNavbarCollapsed && styles.dropdownWrapperCollapsed
  } ${isDropdownOpen ? styles.dropdownVisible : styles.dropdownHidden}`;

  const searchTabItems = useMemo(() => {
    const items = options.map(({ value, tab }) => {
      const query = convertObjectToQueryParam({
        s: inputValue,
        tab,
      });

      return (
        <SearchItem
          key={value}
          value={value}
          text={inputValue}
          userLocation={userLocation}
          params={query}
          closedDropdown={closedDropdown}
          updateLastSearches={updateLastSearches}
        />
      );
    });
    return items;
  }, [inputValue, userLocation]);

  return (
    <div className={dropdownContainerClasses}>
      {!inputValue ? (
        <div className={styles.containerEmptyList}>
          <SearchEmptyList closedDropdown={closedDropdown} lastSearches={lastSearches} updateLastSearches={updateLastSearches} />
        </div>
      ) : (
        <>
          <div className={styles.containerItems}>
            {searchTabItems}
          </div>
          <Configure hitsPerPage={5} />
          <RefinementList attribute="name" />
          <Hits
            classNames={{
              list: styles.HitsList,
            }}
            hitComponent={Hit}
          />
          <div className={styles.SearchButtonResult}>
            <Button fullWidth upperCase={false} onClick={handleShowMore}>
              Ver todos os eventos
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default AlgoliaSearchDropdown;
